import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Home,
  BookOpen,
  Bookmark,
  Brain,
  Target,
  CheckCircle,
  Heart,
  Clock,
  Settings,
  LogOut,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  X,
  Sparkles,
} from "lucide-react";
import { useAuth } from "../../App";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { useLanguage } from "../../App";
import { useSidebar } from "../../contexts/SidebarContext";
import { Button } from "../ui/button";

const SideNav = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { language } = useLanguage();
  const { isCollapsed, isMobileOpen, toggleSidebar, toggleMobileSidebar, closeMobileSidebar } = useSidebar();
  const isEnglish = language === "en";
  const navItems = [
    { path: "/home", icon: Home, label: isEnglish ? "Home" : "Beranda" },
    { path: "/quran", icon: BookOpen, label: isEnglish ? "Quran" : "Quran" },
    { path: "/vocabulary", icon: Sparkles, label: isEnglish ? "Vocab" : "Kosakata" },
    { path: "/hafalan", icon: Brain, label: isEnglish ? "Memorize" : "Hafalan" },
    { path: "/habits", icon: CheckCircle, label: isEnglish ? "Habits" : "Kebiasaan" },
    { path: "/prayer-times", icon: Clock, label: isEnglish ? "Prayer Times" : "Jadwal Shalat" },
    { path: "/doa", icon: Heart, label: isEnglish ? "Duas & Dhikr" : "Doa & Dzikir" },
    { path: "/quiz", icon: Target, label: isEnglish ? "Quiz" : "Quiz" },
    { path: "/bookmarks", icon: Bookmark, label: isEnglish ? "Bookmarks" : "Bookmark" },
    { path: "/analytics", icon: BarChart3, label: isEnglish ? "Analytics" : "Analitik" },
  ];

  const handleLogout = async () => {
    await logout();
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] md:hidden animate-in fade-in duration-200"
          onClick={closeMobileSidebar}
          data-testid="mobile-sidebar-overlay"
        />
      )}

      {/* Mobile Close Button - Premium */}
      {isMobileOpen && (
      <button
        onClick={closeMobileSidebar}
        className="md:hidden fixed top-4 right-4 z-[80] p-3 bg-gradient-to-br from-card to-card/80 backdrop-blur-md rounded-2xl shadow-xl border border-border/50 hover:shadow-2xl hover:scale-105 active:scale-95 transition-all duration-200 group"
        data-testid="mobile-close-btn"
      >
        <X className="w-5 h-5 text-foreground group-hover:text-primary transition-colors" />
      </button>
      )}

      <aside
        className={`side-nav ${isCollapsed ? "collapsed" : ""} ${isMobileOpen ? "mobile-open" : ""}`}
        data-testid="side-nav"
      >
        {/* Toggle Button - Premium Desktop */}
        <button
          onClick={toggleSidebar}
          className="hidden md:flex absolute top-6 -right-4 z-50 w-8 h-8 bg-gradient-to-br from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground rounded-xl items-center justify-center shadow-lg hover:shadow-xl hover:shadow-primary/30 hover:scale-110 active:scale-95 transition-all duration-200 group"
          data-testid="sidebar-toggle-btn"
          title={isCollapsed ? (isEnglish ? "Expand sidebar" : "Perluas sidebar") : (isEnglish ? "Collapse sidebar" : "Kecilkan sidebar")}
        >
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4 group-hover:rotate-90 transition-transform duration-200" />
          ) : (
            <ChevronLeft className="w-4 h-4 group-hover:-rotate-90 transition-transform duration-200" />
          )}
        </button>

        {/* Toggle Button - Desktop Alternative (Bottom) */}
        <button
          onClick={toggleSidebar}
          className="hidden md:flex absolute bottom-6 -right-4 z-50 w-8 h-8 bg-gradient-to-br from-muted to-muted/80 hover:from-muted/90 hover:to-muted/70 text-muted-foreground rounded-xl items-center justify-center shadow-md hover:shadow-lg hover:scale-110 active:scale-95 transition-all duration-200 group border border-border/50"
          data-testid="sidebar-toggle-btn-bottom"
          title={isCollapsed ? (isEnglish ? "Expand sidebar" : "Perluas sidebar") : (isEnglish ? "Collapse sidebar" : "Kecilkan sidebar")}
        >
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4 group-hover:rotate-90 transition-transform duration-200" />
          ) : (
            <ChevronLeft className="w-4 h-4 group-hover:-rotate-90 transition-transform duration-200" />
          )}
        </button>

        {/* Logo */}
        <div className="mb-8">
          <h1 className={`text-2xl font-bold text-primary transition-all duration-300 ${isCollapsed ? "text-center text-xl" : ""}`}>
            {isCollapsed ? "Q" : "Quran"}
          </h1>
          <p className={`text-sm text-muted-foreground transition-all duration-300 overflow-hidden ${isCollapsed ? "hidden" : ""}`}>
            {isEnglish ? "Companion" : "Companion"}
          </p>
        </div>

        {/* User info */}
        {user && (
          <div className={`flex items-center gap-3 mb-8 p-3 bg-muted/50 rounded-xl transition-all duration-300 ${isCollapsed ? "justify-center" : ""}`}>
            <Avatar className="w-10 h-10 flex-shrink-0">
              <AvatarImage src={user.picture} alt={user.name} />
              <AvatarFallback className="bg-primary text-primary-foreground">
                {user.name?.charAt(0) || "U"}
              </AvatarFallback>
            </Avatar>
            <div className={`flex-1 min-w-0 transition-all duration-300 overflow-hidden ${isCollapsed ? "hidden" : ""}`}>
              <p className="font-medium text-sm truncate">{user.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
            </div>
          </div>
        )}

      {/* Navigation */}
      <nav className="flex-1 space-y-1">
        {navItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          const Icon = item.icon;

          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={closeMobileSidebar}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              } ${isCollapsed ? "justify-center" : ""}`}
              title={isCollapsed ? item.label : ""}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              <span className={`font-medium transition-all duration-300 overflow-hidden ${isCollapsed ? "hidden" : ""}`}>
                {item.label}
              </span>
            </NavLink>
          );
        })}
      </nav>

      {/* Bottom actions */}
      <div className="mt-auto pt-4 border-t border-border space-y-1">
        <NavLink
          to="/settings"
          onClick={closeMobileSidebar}
          className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
            location.pathname.startsWith("/settings")
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-muted hover:text-foreground"
          } ${isCollapsed ? "justify-center" : ""}`}
          title={isCollapsed ? (isEnglish ? "Settings" : "Pengaturan") : ""}
        >
          <Settings className="w-5 h-5 flex-shrink-0" />
          <span className={`font-medium transition-all duration-300 overflow-hidden ${isCollapsed ? "hidden" : ""}`}>
            {isEnglish ? "Settings" : "Pengaturan"}
          </span>
        </NavLink>
        <button
          onClick={(e) => {
            handleLogout();
            closeMobileSidebar();
          }}
          className={`flex items-center gap-3 px-4 py-3 rounded-xl w-full text-left text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-all duration-300 ${isCollapsed ? "justify-center" : ""}`}
          data-testid="logout-btn"
          title={isCollapsed ? (isEnglish ? "Logout" : "Keluar") : ""}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          <span className={`font-medium transition-all duration-300 overflow-hidden ${isCollapsed ? "hidden" : ""}`}>
            {isEnglish ? "Logout" : "Keluar"}
          </span>
        </button>
      </div>
    </aside>
    </>
  );
};

export default SideNav;
